import { HeaderService } from 'src/app/components/header/header.service';
import { AgGridRequestService } from 'src/app/services/ag-grid-request.service';
import { Injectable, OnDestroy } from "@angular/core";
import { HttpHeaders, HttpClient } from "@angular/common/http";

import { BehaviorSubject, Observable, Subject, takeUntil } from "rxjs";

import {
  SendToProductionRequestModel,
  MapValidateRequestModel,
  ItemsBulkRequestModel,
  SkuMappingRequestModel,
  EditActiveRequestModel, VendorTypeAhead, SaveVendorItemPayload, ValidateBulkItemsPayload, GetSingleSkuPayload, SaveGetSingleSkuPayload, GetWalmartSingleSkuPayload, setItemMapping,
} from '../models/inventory.model';

@Injectable({
  providedIn: "root",
})
export class InventoryService implements OnDestroy{
  private url = "api/inventory";
  public itemEditDataSync: Subject<any> = new Subject();
  public vendorEditDataSync: Subject<any> = new Subject();
  account: string;
  private destroy$ = new Subject<void>();
  public updateUnmappedSkus$:BehaviorSubject<boolean> = new BehaviorSubject(null);
  private headers = new HttpHeaders({ "Content-Type": "application/json" });
  options = { headers: this.headers };
  getTags:any;

  constructor(private http: HttpClient, private agGridRequestService: AgGridRequestService, private headerService: HeaderService) {
    this.headerService.accountsSelected.pipe(takeUntil(this.destroy$)).subscribe(account => {
      this.account = account[0];
    });
  } 
  
  getAllTags() {
    return this.getAllTagsTypes().map((tagType) => tagType.name);
  }

  getAllTagsTypes() {
    return this.getTags;
  }

  public SendToProduction(
    sendToProductionRequestModel: SendToProductionRequestModel[]
  ) {
    const data = JSON.stringify(sendToProductionRequestModel);
    return this.http.post(`/${this.url}/sendtowh`, data, this.options);
  }

  public MapValidateItem(request: string[]) {
    const data = JSON.stringify(request);
    return this.http.post(`/${this.url}/mapvalidateitem`, data, this.options);
  }

  public ExportVendorList() {
    return this.http.post(`/${this.url}/ExportVendorList`, {}, {
      headers: this.headers,
      observe: "response",
      responseType: "blob",
    });
  }

  public MapValidateSku(request: string[]) {
    const data = JSON.stringify(request);
    return this.http.post(`/${this.url}/mapvalidatesku`, data, this.options);
  }

  public MapValidate(request: MapValidateRequestModel[]) {
    const data = JSON.stringify(request);
    return this.http.post(`/${this.url}/mapvalidate`, data, this.options);
  }

  public Map(request: MapValidateRequestModel[]) {
    const data = JSON.stringify(request);
    return this.http.post(`/${this.url}/mapbulk`, data, this.options);
  }

  public AllUnmapped() {
    return this.http.get(`/${this.url}/allunmapped`, this.options);
  }

  public GetProfitBySkuTotal(){
    return this.http.get(`/${this.url}/GetUnmappedDuplicateSkuCount`, this.options);
  }

  public addInventoryItems(request: any): Observable<any[]> {
    return this.http.post<any[]>(`/${this.url}/newitem`, request, this.options);
  }

  public editInventoryItems(request: any): Observable<any[]> {
    return this.http.put<any[]>(`/${this.url}/edititem`, request, this.options);
  }

  public searchItem(request: string): Observable<any[]> {
    return this.http.get<any[]>(`/${this.url}/searchitem/${request}`);
  }

  public searchVender(request: string): Observable<any[]> {
    return this.http.get<any[]>(`/${this.url}/searchvender/${request}`);
  }

  public searchSku(request: string): Observable<any[]> {
    return this.http.get<any[]>(`/${this.url}/searchsku/${request}`);
  }

  public searchSkuByAccount(sku: string, account: string): Observable<any[]> {
    return this.http.get<any[]>(
      `/${this.url}/searchskubyaccount/${account}/${sku}`
    );
  }

  public typeaheadVendor(request: string): Observable<VendorTypeAhead[]> {
    return this.http.get<VendorTypeAhead[]>(
        `/${this.url}/TypeaheadVendor?Qry=${request}`
    );
  }

  public searchSkuByAccountOversized(
    sku: string,
    account: string,
    isOversized: any
  ): Observable<any[]> {
    return this.http.get<any[]>(
      `/${this.url}/searchskubyaccountoversized/${account}/${sku}/${isOversized}`
    );
  }

  public editNewVendor(request: any): Observable<any[]> {
    return this.http.put<any[]>(
      `/${this.url}/editvendor`,
      request,
      this.options
    );
  }

  public addNewVendor(request: any): Observable<any[]> {
    return this.http.post<any[]>(
      `/${this.url}/newvendor`,
      request,
      this.options
    );
  }

  public MarkupValidate(skus: string[], acc: string) {
    const data = JSON.stringify({ skus, account: acc });
    return this.http.post(`/${this.url}/markupvalidate`, data, this.options);
  }

  public MarkupBulk(
    skus: string[],
    markups: number[],
    markupMins: number[],
    acc: string
  ) {
    const data = JSON.stringify({
      skus,
      markups,
      markupMins,
      account: acc,
    });
    return this.http.post(`/${this.url}/markupbulk`, data, this.options);
  }

  public ItemsValidate(items: string[]) {
    const data = JSON.stringify(items);
    return this.http.post(`/${this.url}/itemsvalidate`, data, this.options);
  }

  public ItemsBulk(itemsBulkRequestModel: ItemsBulkRequestModel[]) {
    const data = JSON.stringify(itemsBulkRequestModel);
    return this.http.post(`/${this.url}/itemsbulk`, data, this.options);
  }

  public SkuMapping(skuMappingRequestModel: SkuMappingRequestModel) {
    const data = JSON.stringify(skuMappingRequestModel);
    return this.http.post(`/${this.url}/skumapping`, data, this.options);
  }

  public EditActive(editActiveRequestModel: EditActiveRequestModel) {
    const data = JSON.stringify(editActiveRequestModel);
    return this.http.post(`/${this.url}/editactive`, data, this.options);
  }


  public EditActiveById(editActiveRequestModel: EditActiveRequestModel) {
    const data = JSON.stringify(editActiveRequestModel);
    return this.http.post(`/${this.url}/EditActiveByAccountSkuId`, data, this.options);
  }

  public GetSkuErrors() {
    return this.http.get(`/${this.url}/sku/errors`, this.options);
  }

  public GetVendorDropShip(id: any) {
    return this.http.get(`/${this.url}/vendordropship/${id}`, this.options);
  }

  public SkuErrorsDismiss(sku: any, account: any, shipgoodtype: any) {
    const data = JSON.stringify({ sku, account, shipgoodtype });
    return this.http.put(`/${this.url}/sku/errors/dismiss`, data, this.options);
  }

  public SkuByAccount(
    account: any,
    inactive: boolean,
    page?: number,
    limit?: number,
    searchText?: string,
    sortColumn?: number,
    sortDesc?: boolean
  ) {
    const data = JSON.stringify({
      Account: account,
      Active: !inactive,
      PageNumber: page,
      RowsPerPage: limit,
      Search: searchText,
      SortColumn: sortColumn,
      SortDesc: sortDesc ? sortDesc : false,
    });
    return this.http.post(`/${this.url}/skubyaccount`, data, this.options);
  }

  public SkuExact(sku: string, account: string) {
    const data = JSON.stringify({
      Search: sku,
      Account: account,
      Exact: true,
    });
    return this.http.post(`/${this.url}/skubyaccount`, data, this.options);
  }

  public ListItemsByFilter(request: any) {
    const data = JSON.stringify(request);
    return this.http.post(`/${this.url}/listitemsbyfilter`, data, this.options);
  }

  public ListVendors(
    page: number,
    limit: number,
    searchText: string,
    sortColumn: number,
    sortDesc: boolean
  ) {
    const data = JSON.stringify({
      PageNumber: page,
      RowsPerPage: limit,
      Search: searchText,
      SortColumn: sortColumn,
      SortDesc: sortDesc,
    });
    return this.http.post(`/${this.url}/listvendors`, data, this.options);
  }

  public SelectVendor(id: string) {
    return this.http.get(`/${this.url}/selectvendor/${id}`, this.options);
  }

  public MappedSkusToItems(id: string) {
    return this.http.get(`/${this.url}/mappedskustoitems/${id}`, this.options);
  }

  public RelatedItems(id: string) {
    return this.http.get(`/${this.url}/relateditems/${id}`, this.options);
  }

  public ItemCodes(id: string) {
    return this.http.get(`/${this.url}/itemcodes/${id}`, this.options);
  }

  public DropShipInventory(id: string) {
    return this.http.get(`/${this.url}/dropshipinventory/${id}`, this.options);
  }

  public LocSummary(request: any) {
    const data = JSON.stringify(request);
    return this.http.post(`/${this.url}/locsummary`, data, this.options);
  }

  public LocHistory(request: any) {
    const data = JSON.stringify(request);
    return this.http.post(`/${this.url}/lochistory`, data, this.options);
  }

  public ProductList(
    page: number,
    perPage: number,
    search = "",
    sortColumn = 1,
    sortOrder = false
  ) {
    const data = JSON.stringify({
      PageNumber: page,
      RowsPerPage: perPage,
      Search: search,
      SortColumn: sortColumn,
      SortDesc: sortOrder,
    });
    return this.http.post(`/${this.url}/productlist`, data, this.options);
  }

  public getProductMappingList = (itemGroup: any) => {
    const url = `/${this.url}/ProductMappingList?ItemGroup=${itemGroup}`;
    return this.http.get(url, this.options);
  };
  public getProductMappingDetails = (itemGroup: any, MapId: string) => {
    const url = `/${this.url}/ProductMappingDetails?ItemGroup=${itemGroup}&MapId=${MapId}`;
    return this.http.get(url, this.options);
  };
  public getProductPurchaseLines = (itemGroup: any) => {
    const url = `/${this.url}/ProductPurchaseLines?ItemGroup=${itemGroup}`;
    return this.http.get(url, this.options);
  };
  public addNewProduct = (payload: any) => {
    const url = `/${this.url}/AddProduct`;
    return this.http.post(url, payload, this.options);
  };
  public addNewPurchaseLine = (payload) => {
    const url = `/${this.url}/AddPurchaseLine`;
    return this.http.post(url, payload, this.options);
  };
  public editMapping = (payload) => {
    const url = `/${this.url}/EditMappings`;
    return this.http.post(url, payload);
  };
  public updateIsoversized = (payload) => {
    const url = `/${this.url}/UpdateIsoversized`;
    return this.http.post(url, payload);
  };

  public ValidateBulkSkus(bulkSkuReq) {
    return this.http.post(
      `/${this.url}/ValidateSkusBulk`,
      bulkSkuReq,
      this.options
    );
  }

  public SaveVendorItem(payload: SaveVendorItemPayload) {
    return this.http.post(
      `/${this.url}/SaveVendorItem`,
      payload,
      this.options
    );
  }

  ValidateBulkItems(payload: ValidateBulkItemsPayload) {
    return this.http.post(
      `/${this.url}/ValidateBulkItems`,
      payload,
      this.options
    );
  }

  RemoveVendorItem(itemVendorId: number) {
    return this.http.post(
      `/${this.url}/RemoveVendorItem`,
      {itemVendorId},
      this.options
    );
  }

  GetStockBreakdown(itemId: number) {
    return this.http.get(
      `/${this.url}/GetStockBreakdown?id=${itemId}`,
      this.options
    );
  }

  GetProductPos(ItemId,IsDeleted?) {
    return this.http.get(`/${this.url}/GetProductPos?Id=${ItemId}${IsDeleted ? "&IsDeleted=" + IsDeleted : "" }`, this.options);
  }

  GetProductShipments(productId) {
    return this.http.get(`/${this.url}/GetProductShipments?ProductId=${productId}`, this.options);
  }
  

  // public TypeaheadVendor(payload: SaveVendorItemPayload) {
  //   return this.http.post(
  //     `/${this.url}/SaveVendorItem`,
  //     payload,
  //     this.options
  //   );
  // }

  ExportMappings() {
    return this.http.post(`/${this.url}/ExportMappings`, {}, {
      headers: this.headers,
      observe: "response",
      responseType: "blob",
    });
  }

  ExportUnMappings() {
    return this.http.post(`/${this.url}/ExportUnmapped`, {}, {
      headers: this.headers,
      observe: "response",
      responseType: "blob",
    });
  }

  SyncHistory(body) {
    const filters = {
      // FulfillmentType: {
      //   filterType: "set",
      //   values: ["Fba"],
      // },
    }
    // if (this.account) {
    //   filters['Account'] = {
    //     filterType: "set",
    //     values: [this.account],
    //   }
    // }
    const payload = this.agGridRequestService.transformAgGridRequest(
      body,
      [],
      '',
      filters
    );
    return this.http.post(`/${this.url}/SyncHistory`, payload, this.options);
  }

  GetProductShipmentList(body, columnDefs,filterModel?) {
    const payload = this.agGridRequestService.transformAgGridRequest(
      body,
      columnDefs,
      "",
      filterModel
    );

    return this.http.post(
      `/${this.url}/GetProductShipmentList`,
      payload,
      this.options
    );
  }

  ItemSalesSummary(productId){
    return this.http.get(`/${this.url}/ItemSalesSummary?ProductId=${productId}`, this.options);
  }

  ItemGraphSummary(payload){
    return this.http.get(`/${this.url}/ItemGraphSummary?ProductId=${payload.productId}&StartDate=${payload.startDate}&EndDate=${payload.endDate}`, this.options);
  }

  ItemProfitSummary(payload){
    return this.http.get(`/${this.url}/ItemProfitSummary?ProductId=${payload.productId}&StartDate=${payload.startDate}&EndDate=${payload.endDate}`, this.options);
  }

  ItemGraphDetail(payload){
    return this.http.get(`/${this.url}/ItemGraphDetail?ProductId=${payload.productId}&StartDate=${payload.startDate}&EndDate=${payload.endDate}`, this.options);
  }

  syncSkuStock(payload){
    return this.http.post(`/${this.url}/SyncSkuStock`,
      payload,
      this.options
    );
  }

  GetSkuWithDuplicateAsin(payload) {
    return this.http.get(`/${this.url}/GetSkuWithDuplicateAsin?Account=${payload.account}&StartRow=${payload.startRow}&EndRow=${payload.endRow}`, this.options);
  }

  getSkuQuickMapping(payload){
    return this.http.get(`/${this.url}/GetSkuQuickMappings?Type=${payload.type}&StartRow=${payload.startRow}&EndRow=${payload.endRow}&Account=${payload.account}`, this.options );
  }

  getuNmappedDuplicatesku(account){
    return this.http.get(`/${this.url}/GetUnmappedDuplicateSkuCount?Account=${account}`, this.options);
  }

  getUnmappedSkuDetail(){
    return this.http.get(`/${this.url}/GetUnmappedSkuDetail`, this.options);
  }

  exportUmpappedSku(type,account){
    return this.http.post(`/${this.url}/ExportUmpappedSku?Type=${type}&Account=${account}`, {}, {
      headers: this.headers,
      observe: "response",
      responseType: "blob",
    });
  } 

  SetItemSkuMapping(payload){
    return this.http.post(`/${this.url}/SetItemSkuMapping`, payload,this.options );
  }

  SetItemSkuLargeRecordMapping(payload){
    return this.http.post(`/${this.url}/SetItemSkuLargeRecordMapping`, payload,this.options );
  }
  
  getSkuDetials(id){
    return this.http.get(`/${this.url}/GetSkuDetials`, this.options);
  }
  
  skuSalesSummary(productId){
    return this.http.get(`/${this.url}/SkuSalesSummary?AccountSkuId=${productId}`, this.options);
  }

  SkuGraphSummary(payload){
    return this.http.get(`/${this.url}/SkuGraphSummary?AccountSkuId=${payload.productId}&StartDate=${payload.startDate}&EndDate=${payload.endDate}`, this.options);
  }


  SkuGraphDetail(payload){
    return this.http.get(`/${this.url}/SkuGraphDetail?AccountSkuId=${payload.productId}&StartDate=${payload.startDate}&EndDate=${payload.endDate}`, this.options);
  }

   GetVendorDetail(id) {
    return this.http.get(`/${this.url}/GetVendorDetail?Id=${id}`, this.options);
  }

  SaveItemWithVendor(payload){
    return this.http.post(`/${this.url}/SaveItemWithVendor`, payload,this.options );
  }
  
  GetSingleSku(payload:GetSingleSkuPayload){
    return this.http.post(`/${this.url}/GetSingleSku`, payload,this.options ); 
  }

  SaveGetSingleSku(payload:SaveGetSingleSkuPayload){
    return this.http.post(`/${this.url}/SaveGetSingleSku`, payload,this.options ); 
  }

  GetWalmartSingleSku(payload:GetWalmartSingleSkuPayload){
    return this.http.post(`/${this.url}/GetWalmartSingleSku`, payload,this.options ); 
  }

  getItemStockWithNoMappingCount(){
    return this.http.get(`/${this.url}/GetItemStockWithNoMappingCount`, this.options);
  }

  ExportProducts() {
    return this.http.get(`/${this.url}/GetItemStockWithNoMappingExport`, {
      headers: this.headers,
      observe: "response",
      responseType: "blob",
    });
  }

  GetTags() {
    return this.http.get(`/${this.url}/GetTags`, this.options);
  }

  AddUpdateTag(payload) {
    return this.http.post(`/${this.url}/AddUpdateTag`, payload, this.options);
  }

  DeleteTag(payload) {
    return this.http.post(`/${this.url}/DeleteTag`, payload, this.options);
  }

  AddTagsInProduct(payload) {
    return this.http.post(`/${this.url}/AddTagsInProduct`, payload, this.options);
  }

  DeleteTagInProduct(payload) {
    return this.http.post(`/${this.url}/DeleteTagInProduct`, payload, this.options);
  }

  getTagProduct(){
    return this.http.get(`/${this.url}/GetTagsProduct`, this.options);
  }

  UpdateCaseRoundingSetting(isEnforceCase){
    return this.http.post(`/${this.url}/UpdateCaseRoundingSetting`,isEnforceCase,this.options)
  }


  mappedWithSyncOffCount(){
    return this.http.get(`/${this.url}/MappedWithSyncOffCount`, this.options);
  }

  getMappedWithSyncOff(){
    return this.http.get(`/${this.url}/MappedWithSyncOffAccount`, this.options);
  }

  exportMappedWithSyncOffSku(account){
    return this.http.post(`/${this.url}/ExportMappedWithSyncOffSku?Account=${account}`, {}, {
      headers: this.headers,
      observe: "response",
      responseType: "blob",
    });
  }

  ValidateBulkVendorItemDelete(payload){
    return this.http.post(`/${this.url}/ValidateBulkVendorItemDelete`,payload,this.options)
  }

  BulkVendorItemDelete(payload){
    return this.http.post(`/${this.url}/BulkVendorItemDelete`,payload,this.options)
  }
  
  GetDuplicateSkuCount(){
    return this.http.get(`/${this.url}/GetDuplicateSkuCount`, this.options);
  }

  SendEmailNotification(userId){
    return this.http.post(`/${this.url}/SendEmailNotification`,{userId},this.options)
  }


  financeSettlementData(id){
    return this.http.get(`/${this.url}/FinanceSettlementData?OrderId=${id}`, this.options);
  }

  recalOrdersYearInsertBm(){
    return this.http.get(`/${this.url}/RecalOrdersYearInsertBm`, this.options);
  }

  SaveReportLogEvent(tenantId){
    return this.http.post(`/${this.url}/SaveReportLogEvent`,{tenantId},this.options)

  }
  setItemMapping(payload:setItemMapping){
    return this.http.post(`/${this.url}/SetItemMapping`,payload,this.options)
  }

  AddTagsInSku(payload) {
    return this.http.post(`/${this.url}/AddTagsInSku`, payload, this.options);
  }

  DeleteTagInSku(payload) {
    return this.http.post(`/${this.url}/DeleteTagInSku`, payload, this.options);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }
  
  BulkAddTagsInSku(payload){
    return this.http.post(`/${this.url}/BulkAddTagsInSku`, payload, this.options);
  }

  BulkAddTagsInProduct(payload){
    return this.http.post(`/${this.url}/BulkAddTagsInProduct`, payload, this.options);
  }

  RemoveTagsInProduct(payload){
    return this.http.post(`/${this.url}/RemoveTagsInProduct`, payload, this.options);
  }

  RemoveTagsInSku(payload){
    return this.http.post(`/${this.url}/RemoveTagsInSku`, payload, this.options);
  }

  getSkuTags(){
    return this.http.get(`/${this.url}/GetSkuTags`, this.options);
  }

  getProductTags(){
    return this.http.get(`/${this.url}/GetProductTags`, this.options);
  }

  removeSkuTags(payload){
    return this.http.post(`/${this.url}/RemoveSkuTags`, payload, this.options);
  }

  removeProductTags(payload){
    return this.http.post(`/${this.url}/RemoveProductTags`, payload, this.options);
  }

  updateVendorItemCaseDim(payload){
    return this.http.post(`/${this.url}/UpdateVendorItemCaseDim`, payload, this.options);
  }

  getSkuNotOnSellerCount(){
    return this.http.get(`/${this.url}/GetSkuNotOnSellerCount`, this.options);
  }

  skuNotExistSellerDetails(){
    return this.http.get(`/${this.url}/SkuNotExistSellerDetails`, this.options);
  }

  exportSkuNotExistSeller(account){
    return this.http.post(`/${this.url}/ExportSkuNotExistSeller?Account=${account}`, {}, {
      headers: this.headers,
      observe: "response",
      responseType: "blob",
    });
  }
  validateItems(payload){
    return this.http.post(`/${this.url}/ValidateaItems`,payload,this.options);

  }

  createQuickProduct(body, columns,globalSearch,productList){
    if (this.account) {
      body.filterModel["Account"] = {
        filterType: "set",
        values: [this.account],
      };
    }
    const payload = this.agGridRequestService.transformAgGridRequest(
      body,
      columns,
      globalSearch,
      null
    );
    payload.type = productList.type
    payload.vendorId = productList.vendorId
    return this.http.post(
      `/${this.url}/CreateQuickProductSignalR`,
      payload,
      this.options
    );
  }
}
